import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/rootReducer";
import { LoadingStatus } from "@/enums/LoadingStatus.enum";
import { ClientDecision, setClientDecisions, setEntityStatus } from "@/store/slices/masterDataSlice";
import { masterDataIdbService } from "@/store/idbStorage/masterDataIdbService";
import type { MasterDataEntityKey } from "@/store/idbStorage/masterDataIdbService";
import { API_ENDPOINTS } from "@/api/endpoints.constant";
import { clivApiRequest } from "@/utils/apiClient";
import { systemConfig } from "@/config/systemConfig";
import * as Sentry from "@sentry/react";

const ENTITY_KEY: MasterDataEntityKey = "md_clientDecisions";

export const useClientDecisions = () => {
  const dispatch = useDispatch();
  const clientDecisions = useAppSelector(
    (state: RootState) => state.masterData.clientDecisions
  );
  const status = useAppSelector(
    (state: RootState) => state.masterData.entityStatus["clientDecisions"] ?? LoadingStatus.IDLE
  );

  useEffect(() => {
    if (clientDecisions?.length || status === LoadingStatus.LOADING) {
      return;
    }

    const load = async () => {
      dispatch(setEntityStatus({ entity: "clientDecisions", status: LoadingStatus.LOADING }));
      try {
        const cached = await masterDataIdbService.getEntity<ClientDecision[]>(ENTITY_KEY);
        if (cached && cached.length) {
          dispatch(setClientDecisions(cached));
          dispatch(setEntityStatus({ entity: "clientDecisions", status: LoadingStatus.SUCCEEDED }));
          return;
        }
        const response = await clivApiRequest<ClientDecision[]>({
          url: `${systemConfig.apiUrl}/${API_ENDPOINTS.META.CLIENT_DECISIONS}`,
        });
        if (response.ok) {
          const data = response.data || [];
          dispatch(setClientDecisions(data));
          await masterDataIdbService.setEntity(ENTITY_KEY, data);
          dispatch(setEntityStatus({ entity: "clientDecisions", status: LoadingStatus.SUCCEEDED }));
        } else {
          dispatch(setEntityStatus({ entity: "clientDecisions", status: LoadingStatus.FAILED }));
        }
      } catch (error) {
        Sentry.captureException(error as Error);
        dispatch(setEntityStatus({ entity: "clientDecisions", status: LoadingStatus.FAILED }));
      }
    };

    void load();
  }, [clientDecisions?.length, status, dispatch]);

  return { clientDecisions, status };
};

